import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Box, Button, FormControlLabel, Radio, RadioGroup, useTheme } from "@mui/material";
import React, { useContext, useEffect, useMemo } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { useTeamAliases } from "@/hooks/useTeamAliases";
import Locator from "@/locator";
import { recipientOptions, recipientsForMode, sameRecipients } from "@/models/fleetMessageRecipients";
import { computeFleetMessageAudiences, fleetMessageAudienceCollections } from "@/models/repositories/Driver";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { FleetMessageRecipientMode } from "@/types/fleetMessage";
import { pluralValue } from "@/utils";

const EMPTY_AUDIENCES = { all: [], mine: [] };

/** The row checkbox rides beside its own step so the sender knows what to look for on the table. */
const TABLE_STEPS = [
  { text: "Filter the table to the drivers you want", icon: false },
  { text: "Check individual rows, or select all", icon: true },
  { text: "Click Fleet Message to return here", icon: false },
];

/** Both buttons hug their label: the theme stretches every button to its container otherwise. */
const RETURN_TO_TABLE_BUTTON_SX = { width: "auto" };

/**
 * Who a fleet message goes to: the driver table's checked rows, one of the two whole-audience shortcuts, or a
 * custom set the sender goes back to the table to build.
 *
 * Feed Groups used to be the prominent choice here and are no longer maintained — a new driver joins no group, so
 * the group tree showed an increasingly wrong picture of the roster (WEB-2272). Every option below reads current
 * roster state instead.
 */
export default React.memo(function FleetMessageRecipients({
  mode,
  onModeChange,
  tableSelection,
  activeChannels,
  onGoToDriverTable,
}: {
  mode: FleetMessageRecipientMode | null;
  onModeChange: (mode: FleetMessageRecipientMode, feedIds: string[]) => void;
  tableSelection: string[];
  activeChannels: string[];
  onGoToDriverTable: () => void;
}) {
  const theme = useTheme();
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const myMembershipId = myCurrentWorkspaceMembership?.id;
  const { teamAliases } = useTeamAliases(workspaceId ?? "", myMembershipId ?? "");
  const teamAliasesKey = teamAliases.join(",");

  const audiences = useCollectionsCompute(
    fleetMessageAudienceCollections,
    () =>
      workspaceId && myMembershipId
        ? computeFleetMessageAudiences({ workspaceId, membershipId: myMembershipId, teamAliases })
        : EMPTY_AUDIENCES,
    [workspaceId, myMembershipId, teamAliasesKey],
    "FleetMessageRecipients:audiences",
  );

  // A whole-audience option names a live set, not a snapshot taken when the radio was clicked: a revoked channel or a
  // newly activated driver has to change what Send carries, not just the count beside the option. This also re-arms
  // the option when the pane reopens on a stored mode, which MUI's RadioGroup cannot do on its own because clicking
  // an already-selected radio fires no change.
  useEffect(() => {
    if (mode !== "all" && mode !== "mine") return;
    const feedIds = recipientsForMode(mode, audiences, tableSelection);
    if (sameRecipients(feedIds, activeChannels)) return;
    onModeChange(mode, feedIds);
  }, [mode, audiences, tableSelection, activeChannels, onModeChange]);

  const options = useMemo(() => recipientOptions(audiences, tableSelection), [audiences, tableSelection]);

  return (
    <Box sx={{ maxHeight: "320px", overflowY: "auto" }}>
      <RadioGroup
        aria-label={Locator.feed.input.fleetMessage.modal.selectRecipients}
        value={mode ?? ""}
        onChange={(event) => {
          const next = event.target.value as FleetMessageRecipientMode;
          onModeChange(next, recipientsForMode(next, audiences, tableSelection));
        }}
      >
        {tableSelection.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <FormControlLabel
              data-sb={TestIds.fleet.recipientOption}
              value="selection"
              control={<Radio color="primary" />}
              label={`${tableSelection.length} selected driver${pluralValue(tableSelection.length)}`}
              sx={{ mr: 0 }}
            />
            <Button
              data-sb={TestIds.fleet.editSelection}
              variant="outlined"
              size="small"
              sx={RETURN_TO_TABLE_BUTTON_SX}
              onClick={onGoToDriverTable}
            >
              Edit selection
            </Button>
          </Box>
        )}
        {options.map((option) => (
          <Box key={option.value}>
            <FormControlLabel
              data-sb={TestIds.fleet.recipientOption}
              value={option.value}
              control={<Radio color="primary" />}
              label={option.label}
            />
            {option.value === "table" && mode === "table" && (
              <Box sx={{ pl: 4, pb: 1, color: theme.palette.neutral.main, fontSize: "14px" }}>
                <Box>Pick a custom set of drivers from the driver table.</Box>
                <Box component="ol" sx={{ pl: 2.5, my: 1 }}>
                  {TABLE_STEPS.map((step) => (
                    <Box component="li" key={step.text}>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {step.text}
                        {step.icon && <CheckBoxIcon sx={{ fontSize: 18 }} />}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mb: 1.5 }}>Your draft will be saved.</Box>
                <Button
                  data-sb={TestIds.fleet.goToDriverTable}
                  variant="outlined"
                  size="small"
                  sx={RETURN_TO_TABLE_BUTTON_SX}
                  onClick={onGoToDriverTable}
                >
                  Go to driver table
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </RadioGroup>
    </Box>
  );
});
